import { supabase } from './supabase';
import { Database } from '../types/supabase';

type BudgetCategory = Database['public']['Tables']['budget_categories']['Row'];
type Budget = Database['public']['Tables']['budgets']['Row'];
type BudgetItem = Database['public']['Tables']['budget_items']['Row'];
type BudgetTransaction = Database['public']['Tables']['budget_transactions']['Row'];
type SavingsGoal = Database['public']['Tables']['savings_goals']['Row'];
type SavingsContribution = Database['public']['Tables']['savings_contributions']['Row'];
type ExpenseEstimator = Database['public']['Tables']['expense_estimators']['Row'];
type BudgetInsight = Database['public']['Tables']['budget_insights']['Row'];

export interface BudgetSummary {
  totalIncome: number;
  totalExpenses: number;
  totalSavings: number;
  netAmount: number;
  savingsRate: number;
}

export interface CategorySummary {
  category: BudgetCategory;
  planned: number;
  actual: number;
  remaining: number;
  percentage: number;
}

export interface SavingsGoalProgress {
  goal: SavingsGoal;
  progress: number;
  daysRemaining: number;
  monthlyTarget: number;
}

export class BudgetingService {
  // Budget Categories
  static async getBudgetCategories(userId: string): Promise<BudgetCategory[]> {
    const { data, error } = await supabase
      .from('budget_categories')
      .select('*')
      .eq('user_id', userId)
      .order('type', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async createBudgetCategory(category: Omit<BudgetCategory, 'id' | 'created_at' | 'updated_at'>): Promise<BudgetCategory> {
    const { data, error } = await supabase
      .from('budget_categories')
      .insert(category)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateBudgetCategory(id: string, updates: Partial<BudgetCategory>): Promise<BudgetCategory> {
    const { data, error } = await supabase
      .from('budget_categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteBudgetCategory(id: string): Promise<void> {
    const { error } = await supabase
      .from('budget_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Budgets
  static async getBudgets(userId: string): Promise<Budget[]> {
    const { data, error } = await supabase
      .from('budgets')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async getActiveBudget(userId: string): Promise<Budget | null> {
    const { data, error } = await supabase
      .from('budgets')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async createBudget(budget: Omit<Budget, 'id' | 'created_at' | 'updated_at'>): Promise<Budget> {
    const { data, error } = await supabase
      .from('budgets')
      .insert(budget)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateBudget(id: string, updates: Partial<Budget>): Promise<Budget> {
    const { data, error } = await supabase
      .from('budgets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteBudget(id: string): Promise<void> {
    const { error } = await supabase
      .from('budgets')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Budget Items
  static async getBudgetItems(budgetId: string): Promise<BudgetItem[]> {
    const { data, error } = await supabase
      .from('budget_items')
      .select(`
        *,
        budget_categories (
          id,
          name,
          type,
          color,
          icon
        )
      `)
      .eq('budget_id', budgetId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async createBudgetItem(item: Omit<BudgetItem, 'id' | 'created_at' | 'updated_at'>): Promise<BudgetItem> {
    const { data, error } = await supabase
      .from('budget_items')
      .insert(item)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateBudgetItem(id: string, updates: Partial<BudgetItem>): Promise<BudgetItem> {
    const { data, error } = await supabase
      .from('budget_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteBudgetItem(id: string): Promise<void> {
    const { error } = await supabase
      .from('budget_items')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Budget Transactions
  static async getBudgetTransactions(userId: string, budgetId?: string): Promise<any[]> {
    let query = supabase
      .from('budget_transactions')
      .select(`
        *,
        budget_categories (
          id,
          name,
          type,
          color,
          icon
        )
      `)
      .eq('user_id', userId)
      .order('transaction_date', { ascending: false });

    if (budgetId) {
      query = query.eq('budget_id', budgetId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching budget transactions:', error);
      throw error;
    }
    return data || [];
  }

  static async createBudgetTransaction(transaction: Omit<BudgetTransaction, 'id' | 'created_at' | 'updated_at'>): Promise<BudgetTransaction> {
    const { data, error } = await supabase
      .from('budget_transactions')
      .insert(transaction)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateBudgetTransaction(id: string, updates: Partial<BudgetTransaction>): Promise<BudgetTransaction> {
    const { data, error } = await supabase
      .from('budget_transactions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteBudgetTransaction(id: string): Promise<void> {
    const { error } = await supabase
      .from('budget_transactions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Savings Goals
  static async getSavingsGoals(userId: string): Promise<SavingsGoal[]> {
    const { data, error } = await supabase
      .from('savings_goals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async createSavingsGoal(goal: Omit<SavingsGoal, 'id' | 'created_at' | 'updated_at'>): Promise<SavingsGoal> {
    const { data, error } = await supabase
      .from('savings_goals')
      .insert(goal)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateSavingsGoal(id: string, updates: Partial<SavingsGoal>): Promise<SavingsGoal> {
    const { data, error } = await supabase
      .from('savings_goals')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteSavingsGoal(id: string): Promise<void> {
    const { error } = await supabase
      .from('savings_goals')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Savings Contributions
  static async getSavingsContributions(goalId: string): Promise<SavingsContribution[]> {
    const { data, error } = await supabase
      .from('savings_contributions')
      .select('*')
      .eq('savings_goal_id', goalId)
      .order('contribution_date', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async createSavingsContribution(contribution: Omit<SavingsContribution, 'id' | 'created_at'>): Promise<SavingsContribution> {
    const { data, error } = await supabase
      .from('savings_contributions')
      .insert(contribution)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Expense Estimators
  static async getExpenseEstimators(userId: string): Promise<ExpenseEstimator[]> {
    const { data, error } = await supabase
      .from('expense_estimators')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async createExpenseEstimator(estimator: Omit<ExpenseEstimator, 'id' | 'created_at' | 'updated_at'>): Promise<ExpenseEstimator> {
    const { data, error } = await supabase
      .from('expense_estimators')
      .insert(estimator)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateExpenseEstimator(id: string, updates: Partial<ExpenseEstimator>): Promise<ExpenseEstimator> {
    const { data, error } = await supabase
      .from('expense_estimators')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteExpenseEstimator(id: string): Promise<void> {
    const { error } = await supabase
      .from('expense_estimators')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Budget Analytics
  static async getBudgetSummary(userId: string, budgetId?: string): Promise<BudgetSummary> {
    const { data: transactions, error } = await supabase
      .from('budget_transactions')
      .select('amount, type')
      .eq('user_id', userId);

    if (error) throw error;

    const totalIncome = transactions
      ?.filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

    const totalExpenses = transactions
      ?.filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

    const totalSavings = transactions
      ?.filter(t => t.type === 'transfer')
      .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

    const netAmount = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;

    return {
      totalIncome,
      totalExpenses,
      totalSavings,
      netAmount,
      savingsRate
    };
  }

  // Decision Support Methods
  static async getDecisionSupportData(userId: string): Promise<{
    canAfford: (amount: number, timeframe: string) => boolean;
    recommendedSavings: number;
    riskAssessment: 'low' | 'medium' | 'high';
    financialHealth: 'excellent' | 'good' | 'fair' | 'poor';
  }> {
    const summary = await this.getBudgetSummary(userId);
    const goals = await this.getSavingsGoalProgress(userId);
    
    // Calculate financial health score
    const savingsRate = summary.savingsRate;
    const debtToIncome = summary.totalExpenses / Math.max(summary.totalIncome, 1);
    
    let financialHealth: 'excellent' | 'good' | 'fair' | 'poor' = 'fair';
    if (savingsRate >= 20 && debtToIncome <= 0.3) financialHealth = 'excellent';
    else if (savingsRate >= 10 && debtToIncome <= 0.5) financialHealth = 'good';
    else if (savingsRate >= 5 && debtToIncome <= 0.7) financialHealth = 'fair';
    else financialHealth = 'poor';

    // Calculate risk assessment
    const emergencyFund = goals.find(g => g.goal.category === 'emergency');
    const hasEmergencyFund = emergencyFund && emergencyFund.progress >= 100;
    const riskAssessment = hasEmergencyFund && savingsRate >= 10 ? 'low' : 
                          savingsRate >= 5 ? 'medium' : 'high';

    // Calculate recommended savings (3-6 months expenses)
    const recommendedSavings = summary.totalExpenses * 4.5; // 4.5 months average

    return {
      canAfford: (amount: number, timeframe: string) => {
        const monthlyAvailable = summary.netAmount;
        const months = timeframe === 'monthly' ? 1 : 
                     timeframe === 'yearly' ? 12 : 
                     timeframe === 'weekly' ? 0.25 : 1;
        return (monthlyAvailable * months) >= amount;
      },
      recommendedSavings,
      riskAssessment,
      financialHealth
    };
  }

  // Smart Recommendations
  static async getSmartRecommendations(userId: string): Promise<{
    type: 'budget_optimization' | 'savings_boost' | 'expense_reduction' | 'goal_acceleration';
    title: string;
    description: string;
    impact: 'high' | 'medium' | 'low';
    action: string;
    estimatedSavings?: number;
  }[]> {
    const summary = await this.getBudgetSummary(userId);
    const categories = await this.getCategorySummaries(userId);
    const goals = await this.getSavingsGoalProgress(userId);
    
    const recommendations = [];

    // Budget optimization recommendations
    if (summary.savingsRate < 10) {
      recommendations.push({
        type: 'savings_boost' as const,
        title: 'Increase Your Savings Rate',
        description: `Your current savings rate is ${summary.savingsRate.toFixed(1)}%. Aim for at least 10-20% for better financial health.`,
        impact: 'high' as const,
        action: 'Review your budget and identify areas to reduce spending',
        estimatedSavings: summary.totalIncome * 0.05 // 5% increase
      });
    }

    // Expense reduction recommendations
    const topExpense = categories.sort((a, b) => b.actual - a.actual)[0];
    if (topExpense && topExpense.percentage > 100) {
      recommendations.push({
        type: 'expense_reduction' as const,
        title: `Reduce ${topExpense.category?.name} Spending`,
        description: `You're spending ${topExpense.percentage.toFixed(0)}% of your budget on ${topExpense.category?.name}.`,
        impact: 'medium' as const,
        action: 'Look for ways to reduce this category by 10-20%',
        estimatedSavings: topExpense.actual * 0.15
      });
    }

    // Goal acceleration recommendations
    const slowGoal = goals.find(g => g.monthlyTarget > 0 && g.monthlyTarget > summary.netAmount * 0.3);
    if (slowGoal) {
      recommendations.push({
        type: 'goal_acceleration' as const,
        title: `Accelerate ${slowGoal.goal.name} Goal`,
        description: `Your monthly target of $${slowGoal.monthlyTarget.toFixed(0)} may be too ambitious. Consider adjusting the timeline.`,
        impact: 'medium' as const,
        action: 'Review your goal timeline or increase monthly contributions',
        estimatedSavings: 0
      });
    }

    return recommendations;
  }

  static async getCategorySummaries(userId: string, budgetId?: string): Promise<CategorySummary[]> {
    const { data: items, error } = await supabase
      .from('budget_items')
      .select(`
        planned_amount,
        actual_amount,
        budget_categories (
          id,
          name,
          type,
          color,
          icon
        )
      `)
      .eq('budget_id', budgetId || '');

    if (error) throw error;

    return items?.map(item => ({
      category: item.budget_categories,
      planned: Number(item.planned_amount),
      actual: Number(item.actual_amount),
      remaining: Number(item.planned_amount) - Number(item.actual_amount),
      percentage: Number(item.planned_amount) > 0 
        ? (Number(item.actual_amount) / Number(item.planned_amount)) * 100 
        : 0
    })) || [];
  }

  static async getSavingsGoalProgress(userId: string): Promise<SavingsGoalProgress[]> {
    const goals = await this.getSavingsGoals(userId);
    if (goals.length === 0) return [];

    const goalIds = goals.map(g => g.id);
    const { data: allContributions, error } = await supabase
      .from('savings_contributions')
      .select('savings_goal_id, amount')
      .in('savings_goal_id', goalIds);

    if (error) throw error;

    const contributionsByGoal = new Map<string, number>();
    for (const c of allContributions || []) {
      const current = contributionsByGoal.get(c.savings_goal_id) || 0;
      contributionsByGoal.set(c.savings_goal_id, current + Number(c.amount));
    }

    return goals.map(goal => {
      const totalContributions = contributionsByGoal.get(goal.id) || 0;

      const progress = Number(goal.target_amount) > 0 
        ? (totalContributions / Number(goal.target_amount)) * 100 
        : 0;

      const daysRemaining = goal.target_date 
        ? Math.max(0, Math.ceil((new Date(goal.target_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
        : 0;

      const monthlyTarget = daysRemaining > 0 && Number(goal.target_amount) > totalContributions
        ? (Number(goal.target_amount) - totalContributions) / Math.max(1, Math.ceil(daysRemaining / 30))
        : 0;

      return { goal, progress, daysRemaining, monthlyTarget };
    });
  }

  // Budget Insights
  static async getBudgetInsights(userId: string): Promise<BudgetInsight[]> {
    const { data, error } = await supabase
      .from('budget_insights')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async createBudgetInsight(insight: Omit<BudgetInsight, 'id' | 'created_at'>): Promise<BudgetInsight> {
    const { data, error } = await supabase
      .from('budget_insights')
      .insert(insight)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async markInsightAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('budget_insights')
      .update({ is_read: true })
      .eq('id', id);

    if (error) throw error;
  }
}
